import { useContext, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import UserContext from "context/UserContext";
import { ROUTES } from "constants/routes";
import { GESTION_ROUTES } from "constants/gestion-routes";
import {
  availableDashboardModes,
  DASHBOARD_MODE_STORAGE_KEY,
  DASHBOARD_MODES,
  resolveDashboardMode,
} from "constants/dashboard-mode";
import { useGestionCustomers } from "hooks/useGestionCustomers";

function readStoredMode() {
  try {
    return localStorage.getItem(DASHBOARD_MODE_STORAGE_KEY);
  } catch (error) {
    return null;
  }
}

export function persistDashboardMode(mode) {
  try {
    localStorage.setItem(DASHBOARD_MODE_STORAGE_KEY, mode);
  } catch (error) {
    // Ignore private-mode storage failures.
  }
}

export function useDashboardMode() {
  const location = useLocation();
  const history = useHistory();
  const { user, selectedGestionCustomer } = useContext(UserContext);
  const { hasGestionAccess, gestionCustomers, gestionCustomersLoaded } = useGestionCustomers();

  const query = new URLSearchParams(location.search);
  const queryMode = query.get("mode");
  const isRevaAdmin = Boolean(user?.is_reva_admin);
  const hasOrganizations = Array.isArray(user?.organizations) && user.organizations.length >= 1;
  const available = availableDashboardModes({ isRevaAdmin, hasGestionAccess, hasOrganizations });
  const resolvedMode = resolveDashboardMode({
    queryMode,
    storedMode: readStoredMode(),
    available,
  });

  const pathMode = location.pathname.startsWith(ROUTES.ADMIN.GESTION)
    ? DASHBOARD_MODES.GESTION
    : location.pathname.startsWith(ROUTES.ADMIN.ORGANIZATIONS)
      ? DASHBOARD_MODES.ORG
      : null;
  const mode = pathMode && available.includes(pathMode) ? pathMode : resolvedMode;

  const gestionHomePath = useMemo(() => {
    const customerId = selectedGestionCustomer?.id || gestionCustomers[0]?.id;
    return customerId ? GESTION_ROUTES.dashboard(String(customerId)) : ROUTES.ADMIN.GESTION;
  }, [selectedGestionCustomer?.id, gestionCustomers]);

  const redirectTo = useMemo(() => {
    if (resolvedMode === DASHBOARD_MODES.GESTION) return gestionHomePath;
    if (resolvedMode === DASHBOARD_MODES.ORG) return ROUTES.ADMIN.ORGANIZATIONS;
    return null;
  }, [resolvedMode, gestionHomePath]);

  const selectMode = (nextMode) => {
    persistDashboardMode(nextMode);
    if (nextMode === DASHBOARD_MODES.GESTION) {
      history.push(gestionHomePath);
      return;
    }
    if (nextMode === DASHBOARD_MODES.ORG) {
      history.push(ROUTES.ADMIN.ORGANIZATIONS);
      return;
    }
    history.push(`${ROUTES.ADMIN.DASHBOARD}?mode=${nextMode}`);
  };

  return {
    mode,
    available,
    redirectTo,
    selectMode,
    showSwitcher: available.length > 1,
    ready: isRevaAdmin || gestionCustomersLoaded,
    gestionCustomerName: gestionCustomers[0]?.name,
  };
}
